import { Component, OnInit, OnDestroy, ElementRef, ViewChild } from '@angular/core';

import { 
  REPORT_CATEGORIES, 
  SUPERSET_CONFIG, 
  ReportCategory, 
  ReportConfig 
} from './reports.config';

// Declare the Superset SDK (loaded via script tag)
declare const supersetEmbeddedSdk: any;

@Component({
  selector: 'app-skyplus-reports',
  templateUrl: './skyplus-reports.component.html',
  styleUrls: ['./skyplus-reports.component.scss']
})
export class SkyPlusReportsComponent implements OnInit, OnDestroy {
  
  @ViewChild('supersetContainer', { static: false }) supersetContainer!: ElementRef;
  
  // Report categories from config
  reportCategories: ReportCategory[] = REPORT_CATEGORIES;
  
  // Currently selected report
  selectedReport: ReportConfig | null = null;
  selectedCategory: ReportCategory | null = null;
  
  // UI State
  isLoading = false;
  errorMessage = '';
  statusMessages: { message: string; isError: boolean }[] = [];
  
  // Superset tokens
  private accessToken = '';
  private guestToken = '';
  
  // User info
  currentUser = 'admin';
  
  constructor() {}

  ngOnInit(): void {
    // Load Superset SDK if not already loaded
    this.loadSupersetSdk();
    
    // Auto-select first report
    if (this.reportCategories.length > 0 && this.reportCategories[0].reports.length > 0) {
      this.selectReport(this.reportCategories[0].reports[0], this.reportCategories[0]);
    }
  }

  ngOnDestroy(): void {
    // Cleanup if needed
  }

  // Load Superset Embedded SDK
  private loadSupersetSdk(): void {
    if (typeof supersetEmbeddedSdk !== 'undefined') {
      return; // Already loaded
    }
    
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/@superset-ui/embedded-sdk@0.1.0-alpha.10/bundle/index.js';
    script.async = true;
    script.onload = () => {
      //this.log('Superset SDK loaded');
    };
    script.onerror = () => {
      this.log('Failed to load Superset SDK', true);
    };
    document.head.appendChild(script);
  }

  // Toggle category expansion
  toggleCategory(category: ReportCategory): void {
    category.expanded = !category.expanded;
  }

  // Select a report
  selectReport(report: ReportConfig, category: ReportCategory): void {
    this.selectedReport = report;
    this.selectedCategory = category;
    this.statusMessages = [];
    this.errorMessage = '';
    
    // Expand the parent category
    category.expanded = true;
    
    // Load the dashboard
    if (report.supersetEmbeddedUuid) {
      this.embedDashboard(report);
    } else {
      this.errorMessage = 'This report has not been configured yet. Please add the Superset Embedded UUID in reports.config.ts';
    }
  }

  // Embed Superset Dashboard
  async embedDashboard(report: ReportConfig): Promise<void> {
    this.isLoading = true;
    this.statusMessages = [];
    
    try {
      // Step 1: Authenticate with Superset
      //this.log('Authenticating with Superset...');
      
      const loginResponse = await fetch(`${SUPERSET_CONFIG.domain}/api/v1/security/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: SUPERSET_CONFIG.username,
          password: SUPERSET_CONFIG.password,
          provider: SUPERSET_CONFIG.provider
        })
      });
      
      if (!loginResponse.ok) {
        throw new Error(`Authentication failed: ${loginResponse.statusText}`);
      }
      
      const loginData = await loginResponse.json();
      this.accessToken = loginData.access_token;
     // this.log('✓ Authentication successful');
      
      // Step 2: Generate guest token
      //this.log('Generating guest token...');
      
      const guestTokenResponse = await fetch(`${SUPERSET_CONFIG.domain}/api/v1/security/guest_token/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.accessToken}`
        },
        body: JSON.stringify({
          user: {
            username: this.currentUser,
            first_name: 'Report',
            last_name: 'Viewer'
          },
          resources: [{
            type: 'dashboard',
            id: report.supersetDashboardId
          }],
          rls: []
        })
      });
      
      if (!guestTokenResponse.ok) {
        throw new Error(`Guest token generation failed: ${guestTokenResponse.statusText}`);
      }
      
      const guestTokenData = await guestTokenResponse.json();
      this.guestToken = guestTokenData.token;
      //this.log(`✓ Guest token generated`);
      
      // Step 3: Embed the dashboard
      //this.log('Embedding dashboard...');
      
      // Wait for container to be available
      setTimeout(() => {
        this.doEmbed(report);
      }, 100);
      
    } catch (error: any) {
      this.log(`✗ Error: ${error.message}`, true);
      this.errorMessage = error.message;
      this.isLoading = false;
    }
  }

  private doEmbed(report: ReportConfig): void {
    const container = document.getElementById('superset-container');
    if (!container) {
      this.log('Container not found', true);
      this.isLoading = false;
      return;
    }
    
    // Clear previous dashboard
    container.innerHTML = '';
    
    try {
      supersetEmbeddedSdk.embedDashboard({
        id: report.supersetEmbeddedUuid,
        supersetDomain: SUPERSET_CONFIG.domain,
        mountPoint: container,
        fetchGuestToken: () => this.guestToken,
        dashboardUiConfig: {
          hideTitle: false,
          hideChartControls: false,
          hideTab: false,
          filters: {
            expanded: false
          }
        }
      });
      
      //this.log('✓ Dashboard embedded successfully!');
    } catch (error: any) {
      this.log(`✗ Embed error: ${error.message}`, true);
    }
    
    this.isLoading = false;
  }

  // Logging helper
  private log(message: string, isError = false): void {
    console.log(message);
    this.statusMessages.push({ message, isError });
  }

  // Navigation - same pattern as document library
  // Check if report is selected
  isReportSelected(report: ReportConfig): boolean {
    return this.selectedReport?.id === report.id;
  }

  // Get report count for a category
  getReportCount(category: ReportCategory): number {
    return category.reports.length;
  }
}